import { ButtonDropdownMultiple, IconButton, Tooltip } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { REVIEW_ENTRIES_TOGGLEABLE_COLUMNS } from '../../../../constants/reviewEntriesToggleableColumns'

interface ReviewEntriesColumnVisibilityMenuProps {
  hiddenColumnIds: string[]
  onHiddenColumnIdsChange: (hiddenColumnIds: string[]) => void
}

export const ReviewEntriesColumnVisibilityMenu = ({
  hiddenColumnIds,
  onHiddenColumnIdsChange,
}: ReviewEntriesColumnVisibilityMenuProps): ReactElement => {
  const { t } = useTranslation()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const items = useMemo(
    () =>
      REVIEW_ENTRIES_TOGGLEABLE_COLUMNS.map(({ id, labelKey }) => ({
        children: t(labelKey),
        id,
        value: id,
      })),
    [t],
  )

  const selectedValues = useMemo(
    () => items.filter(({ value }) => !hiddenColumnIds.includes(value)).map(({ value }) => value),
    [hiddenColumnIds, items],
  )

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true)
  }, [])

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleSelect = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, selectedIds: string[]) => {
      const nextHiddenColumnIds = REVIEW_ENTRIES_TOGGLEABLE_COLUMNS.map(({ id }) => id).filter(
        (id) => !selectedIds.includes(id),
      )
      onHiddenColumnIdsChange(nextHiddenColumnIds)
    },
    [onHiddenColumnIdsChange],
  )

  return (
    <Tooltip forceHidden={isDropdownOpen} label={t('table_column_toggler.toggle_columns')} placement="bottom">
      <ButtonDropdownMultiple
        items={items}
        onClose={handleDropdownClose}
        onOpen={handleDropdownOpen}
        onSelect={handleSelect}
        placement="bottom-start"
        selectedValues={selectedValues}
        size="md"
        triggerElement={
          <IconButton
            aria-label={t('table_column_toggler.toggle_columns')}
            icon="columnsCog"
            size="md"
            variant="tonal"
          />
        }
      />
    </Tooltip>
  )
}
